import React from 'react';
import Button from '@mui/material/Button';
import { WrappingSizeOption, DeliveryOption } from "../../../libs/types";
import CloseIcon from '@mui/icons-material/Close';
import styles from './WrappingFee.module.scss'
import { str2number, formatNumber, formatCurrency } from '../../../libs/helpers';
import DateCell from '../../core/DateCell';
import GeneralDialog from '../../core/GeneralDialog';
import InputCell from '../../core/InputCell';
import { InvoiceMasterData } from '../../../apis/models/InvoiceMasterRequest';
import { WrappingFeeShippingOptionItem, WrappingFeeSizeOptionItem } from '../../../apis/models/WrappingFeeResponse';
import DateWithoutTime from '../../../libs/dateWithoutTime';
import WrappingFeeDialog, { WrappingFeeAddDialogData } from '../../dialogs/invoiceDetails/WrappingFeeDialog';
import { InvStatus } from '../../../libs/constants';

type WrappingSizeType = {
  wrappingSizeOptionId: string;
  wrappingSizeOption: WrappingFeeSizeOptionItem;
  quantity: string;
}

type DeliveryMethodType = {
  deliveryMethodOptionId: string;
  deliveryMethodOption: WrappingFeeShippingOptionItem,
  quantity: string;
}

type InputField = {
  dateAdded: DateWithoutTime;
  memo: string;
  differenceInSalesNumber: string;
  wrappingSizeList: WrappingSizeType[];
  deliveryMethodList: DeliveryMethodType[];
}

type ErrorInfo = {
  dateAdded?: string;
  total?: string;
  compareSizeAndDelivery?: string;
  differenceInSalesNumber?: string;
  sizeErrors: WrappingSizeType[];
  deliveryErrors: DeliveryMethodType[];
}

export type WrappingFeeTabType = InputField & {
  error: ErrorInfo
}

const WrappingFee = (props: WrappingFeeProps) => {
  const [openWarningDialog, setOpenWarningDialog] = React.useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = React.useState<boolean>(false);

  const hasError = React.useMemo(() => {
    return props.tabValues.some(fee => {
      if (Object.keys(fee.error).length !== 0) {
        return (
          fee.error.dateAdded
          || fee.error.total 
          || fee.error.compareSizeAndDelivery 
          || fee.error.differenceInSalesNumber 
          || fee.error.sizeErrors.length > 0
          || fee.error.deliveryErrors.length > 0
        )
      } else return false;
    })
  },[props.tabValues]);

  const validateRow = (item: WrappingFeeTabType) => {
    const errorSizeList = item.wrappingSizeList
      .map(element => {
        return {
          wrappingSizeOptionId: element.wrappingSizeOptionId,
          wrappingSizeOption: element.wrappingSizeOption,
          quantity: validateNumber(element.quantity),
        } as WrappingSizeType
      })
      .filter(fee => fee.quantity !== undefined)

    const errorDeliveryList = item.deliveryMethodList
      .map(element => {
        return {
          deliveryMethodOptionId: element.deliveryMethodOptionId,
          deliveryMethodOption: element.deliveryMethodOption,
          quantity: validateNumber(element.quantity),
        } as DeliveryMethodType
      })
      .filter(fee => fee.quantity !== undefined)

    const sizeTotal = calculateSizeTotal(item)
    const deliveryTotal = calculateDeliveryTotal(item)

    const error: ErrorInfo = {
      dateAdded: validateDateAdded(item.dateAdded),
      total: sizeTotal <= 0 ? "個数を入力してください" : undefined,
      compareSizeAndDelivery: sizeTotal !== deliveryTotal ? "発送数量が合計数と一致していません" : undefined,
      differenceInSalesNumber: validateNumber(item.differenceInSalesNumber),
      sizeErrors: errorSizeList,
      deliveryErrors: errorDeliveryList,
    }
    return error
  }

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else if (props.tabValues.filter(item => item.dateAdded.equals(value)).length > 1) {
      return "日付が重複しています"
    } else {
      const invoiceFrom = new DateWithoutTime(props.targetInvoice.from)
      const invoiceTo = new DateWithoutTime(props.targetInvoice.to)
      if (dateString < invoiceFrom.getValue4Display() || invoiceTo.getValue4Display() < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }
  
  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return undefined
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const calculateSizeTotal = (rowData: WrappingFeeTabType) => {
    return rowData.wrappingSizeList.reduce((prev, element) => prev + str2number(element.quantity), 0)
  }

  const calculateDeliveryTotal = (rowData: WrappingFeeTabType) => {
    return rowData.deliveryMethodList.reduce((prev, element) => prev + str2number(element.quantity), str2number(rowData.differenceInSalesNumber))
  }

  const handleDateChange = (rowIndex: number, date: Date | null) => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        fee.dateAdded = new DateWithoutTime(date);
      }
      fee.error = validateRow(fee)
      return fee;
    })
    props.onChange(newItem);
  }

  const handleMemoChange = (rowIndex: number, value: string | number) => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        fee.memo = value + "";
      }
      fee.error = validateRow(fee)
      return fee;
    })
    props.onChange(newItem);
  }

  const handleDifferenceInSalesNumberChange = (rowIndex: number, value: string | number) => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        fee.differenceInSalesNumber = String(value);
        reCalcDefaultDelivery(fee);
      }
      fee.error = validateRow(fee)
      return fee;
    })

    props.onChange(newItem);
  }

  const handleSizeInputChange = (rowIndex: number, colIndex: number, val: number) => {
    const optionId = sizeOptionMaster[colIndex].optionId;
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        if (isNaN(val)) {
          fee.wrappingSizeList = fee.wrappingSizeList.filter(item => item.wrappingSizeOptionId !== optionId)
        } else {
          const feeData = fee.wrappingSizeList.find(item => item.wrappingSizeOptionId === optionId)
          if (feeData) {
            fee.wrappingSizeList = fee.wrappingSizeList.map(item => {
              if (item.wrappingSizeOptionId === optionId) {
                item.quantity = String(val);
              }
              return item;
            })
          } else {
            fee.wrappingSizeList = fee.wrappingSizeList.concat([{
              wrappingSizeOptionId: optionId,
              wrappingSizeOption: sizeOptionMaster[colIndex],
              quantity: String(val),
            } as WrappingSizeType])
          }
        }
        reCalcDefaultDelivery(fee);
      }
      fee.error = validateRow(fee)
      return fee;
    })

    props.onChange(newItem);
  }

  const handleDeliveryInputChange = (rowIndex: number, colIndex: number, val: number) => {
    const optionId = deliveryOptionMaster[colIndex].optionId;
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        if (isNaN(val)) {
          fee.deliveryMethodList = fee.deliveryMethodList.filter(item => item.deliveryMethodOptionId !== optionId)
        } else {
          const feeData = fee.deliveryMethodList.find(item => item.deliveryMethodOptionId === optionId)
          if (feeData) {
            fee.deliveryMethodList = fee.deliveryMethodList.map(item => {
              if (item.deliveryMethodOptionId === optionId) {
                item.quantity = String(val);
              }
              return item;
            })
          } else {
            fee.deliveryMethodList = fee.deliveryMethodList.concat([{
              deliveryMethodOptionId: optionId,
              deliveryMethodOption: deliveryOptionMaster[colIndex],
              quantity: String(val),
            } as DeliveryMethodType])
          }
        }
        reCalcDefaultDelivery(fee);
      }
      fee.error = validateRow(fee)
      return fee;
    })

    props.onChange(newItem);
  }

  const onDelete = (rowIndex: number) => {
    props.onChange(props.tabValues.filter((item, index) => index !== rowIndex))
  }

  const onSaveByOfficeWorkDialog = (dialogData: WrappingFeeAddDialogData[]) => {
    const addDatas = dialogData.map(d => {
      return {
        dateAdded: d.dateAdded,
        memo: d.remarks,
        differenceInSalesNumber: d.differenceInSalesNumber,
        wrappingSizeList: d.sizeList,
        deliveryMethodList: d.deliveryList,
        error: {}
      } as WrappingFeeTabType
    });
    props.onChange(props.tabValues.concat(addDatas));
    setOpenAddDialog(false)
  }

  const reCalcDefaultDelivery = (rowData: WrappingFeeTabType) => {
    const defaultOption = deliveryOptionMaster.find(element => element.isDefault === 1);
    if (!defaultOption) {
      return;
    }
    const {numOfSize, numOfDelivery} = sumByRow(rowData);

    const defaultQuantity = rowData.deliveryMethodList.find(fee => fee.deliveryMethodOptionId === defaultOption.optionId)?.quantity;
    let calcValue = numOfSize - (numOfDelivery - str2number(defaultQuantity));

    const feeData = rowData.deliveryMethodList.find(item => item.deliveryMethodOptionId === defaultOption.optionId)
    if (feeData) {
      feeData.quantity = String(calcValue);
    } else {
      rowData.deliveryMethodList.push({
        deliveryMethodOptionId: defaultOption.optionId,
        deliveryMethodOption: defaultOption,
        quantity: String(calcValue),
      })
    }
  }

  const sumBySizeOption = (optionId: string) => {
    let total = 0;
    props.tabValues.forEach(rowItem => {
      const fee = rowItem.wrappingSizeList.find(fee => fee.wrappingSizeOptionId === optionId);
      total = total + str2number(fee?.quantity)
    })
    return total;
  }

  const sumByDeliveryOption = (optionId: string) => {
    let total = 0;
    props.tabValues.forEach(rowItem => {
      const fee = rowItem.deliveryMethodList.find(fee => fee.deliveryMethodOptionId === optionId);
      total = total + str2number(fee?.quantity)
    })
    return total;
  }

  const sumDifferenceInSalesNumber = () => {
    return props.tabValues.reduce((prev, rowItem) => {
      return prev + str2number(rowItem.differenceInSalesNumber)
    }, 0)
  }

  const sumByRow = (rowData: WrappingFeeTabType) => {
    const numOfSize = rowData.wrappingSizeList
      .filter(fee => fee.wrappingSizeOptionId !== null)
      .reduce((prev, fee) => prev + str2number(fee.quantity), 0);
    const numOfDelivery = rowData.deliveryMethodList
      .filter(fee => fee.deliveryMethodOptionId !== null)
      .reduce((prev, fee) => prev + str2number(fee.quantity), str2number(rowData.differenceInSalesNumber));

    return {numOfSize, numOfDelivery}
  }

  const getSizeCellValue = (rowItem: WrappingFeeTabType, optionId: string) => {
    const fee = rowItem.wrappingSizeList.find(fee => fee.wrappingSizeOptionId === optionId);
    return fee ? fee.quantity : ""
  }

  const getDeliveryValue = (rowItem: WrappingFeeTabType, optionId: string) => {
    const fee = rowItem.deliveryMethodList.find(fee => fee.deliveryMethodOptionId === optionId);
    return fee ? fee.quantity : ""
  }

  const totalInfo = React.useMemo(() => {
    let price = 0;
    let quantity = 0;
    props.tabValues.forEach(rowItem => {
      rowItem.wrappingSizeList.forEach(fee => {
        if(fee.wrappingSizeOption !== undefined) {
          price = price + str2number(fee.quantity) * fee.wrappingSizeOption.wrappingSizeUnitPrice;
          quantity = quantity + str2number(fee.quantity);
        }
      })
    })
    return {price, quantity};
  }, [props.tabValues]);

  const wrappingExistDates = React.useMemo(() => {
    const existDates: DateWithoutTime[] = [];
    props.tabValues.forEach(d => {
      if (existDates.findIndex(ed => ed.equals(d.dateAdded)) >= 0) {
        return
      }
      existDates.push(d.dateAdded)
    });

    return existDates
  }, [props.tabValues])

  const sizeOptionMaster = React.useMemo(() => {
    return props.wrappingSizeOptions
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0))
      .map(o => {
        return {
          optionId: o.wrappingSizeOptionId!,
          wrappingSize: o.wrappingSizeOption.wrappingSize,
          wrappingSizeUnitPrice: o.wrappingSizeOption.wrappingSizeUnitPrice,
        }
      });
  }, [props.wrappingSizeOptions]);

  const deliveryOptionMaster = React.useMemo(() => {
    return props.wrappingDeliveryOptions
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0))
      .map(o => {
        return {
          optionId: o.deliveryMethodOptionId!,
          deliveryMethod: o.deliveryMethodOption.deliveryMethod,
          isDefault: o.defaultFlag,
          deliveryMethodUnitPrice: 0,
        }
      });
  }, [props.wrappingDeliveryOptions]);

  const isConfirmed = React.useMemo(() => {
    return props.targetInvoice?.status === InvStatus.CONFIRMED || props.targetInvoice?.status === InvStatus.EXPORT;
  }, [props.targetInvoice])

return (<>
    <div className={styles.wrappingFee_container}>
      <div className={styles.totalAmount}>
        <div className={styles.totalAmount__left}>
          <div>合計金額：<span>{formatCurrency(totalInfo.price)}</span></div>
          <div>合計数：<span>{formatNumber(totalInfo.quantity)}個</span></div>
        </div>
        {isConfirmed ? <Button variant="outlined" onClick={() => setOpenWarningDialog(true)}>編集</Button> : <></>}
      </div>
      <div className={styles.table_wrapper}>
        <table>
          <thead>
            <tr className={styles.category}>
              <th colSpan={2}>カテゴリー</th>
              <th colSpan={sizeOptionMaster.length}>サイズ</th>
              <th colSpan={deliveryOptionMaster.length+1}>発送方法</th>
              <th>備考</th>
              {!isConfirmed ? <th rowSpan={5}></th> : <></>}
            </tr>
            <tr>
              <td colSpan={2}>項目</td>
              {sizeOptionMaster.map(data => <td key={data.optionId}>{data.wrappingSize}</td>)}
              {deliveryOptionMaster.map(data => <td key={data.optionId}>{data.deliveryMethod}</td>)}
              <td>販売による<br />個口数差数</td>
              <td>-</td>
            </tr>
            <tr className={styles.price}>
              <td colSpan={2}>単価</td>
              {sizeOptionMaster.map(data => <td key={data.optionId}>{data.wrappingSizeUnitPrice}</td>)}
              {deliveryOptionMaster.map(data => <td key={data.optionId}>-</td>)}
              <td>-</td>
              <td>-</td>
            </tr>
            <tr className={styles.total}>
              <td colSpan={2}>合計使用数</td>
              {sizeOptionMaster.map(data => <td key={data.optionId}>{sumBySizeOption(data.optionId)}</td>)}
              {deliveryOptionMaster.map(data => <td key={data.optionId}>{sumByDeliveryOption(data.optionId)}</td>)}
              <td>{sumDifferenceInSalesNumber()}</td>
              <td>-</td>
            </tr>
            <tr className={styles.total}>
              <td colSpan={2}>合計金額</td>
              {sizeOptionMaster.map(data => <td key={data.optionId}>{formatNumber(data.wrappingSizeUnitPrice * sumBySizeOption(data.optionId))}</td>)}
              {deliveryOptionMaster.map(data => <td key={data.optionId}>-</td>)}
              <td>-</td>
              <td>-</td>
            </tr>
          </thead>
          <tbody>
            {props.tabValues.map((rowData, rowIndex) => {
              const { numOfSize, numOfDelivery} = sumByRow(rowData);
              return (
                <tr key={rowIndex} className={styles.data} style={{backgroundColor: numOfSize !== numOfDelivery ? "#EB4E4E33" : ""}}>
                  <td className={!isConfirmed ? `${styles.unconfirmed}` : ""} style={{minWidth: "142px"}}>
                    <DateCell key={rowIndex}
                      isConfirmed={isConfirmed}
                      onChangeDate={(date) => {handleDateChange(rowIndex, date)}}
                      value={rowData.dateAdded.getValue4Display()}
                    />
                  </td>
                  <td className={styles.row_total}>{numOfSize}</td>
                  {
                    sizeOptionMaster.map((option, sizeColIndex) => (
                      <td key={sizeColIndex} className={!isConfirmed ? `${styles.unconfirmed}` : ""} style={{minWidth: "50px"}}>
                        <InputCell
                          isConfirmed={isConfirmed}
                          type="number"
                          onChangeText={(val) => handleSizeInputChange(rowIndex, sizeColIndex, Number(val))}  
                          value={getSizeCellValue(rowData, option.optionId)}
                          textAlignment="right"
                        />
                      </td>
                    ))
                  }
                  {
                    deliveryOptionMaster.map((option, deliveryColIndex) => (
                      <td key={deliveryColIndex} className={!isConfirmed ? `${styles.unconfirmed}` : ""} style={{minWidth: "50px"}}>
                        <InputCell
                          isConfirmed={isConfirmed}
                          type="number"
                          onChangeText={(val) => handleDeliveryInputChange(rowIndex, deliveryColIndex, Number(val))}
                          value={getDeliveryValue(rowData, option.optionId)}
                          textAlignment="right"/>
                      </td>
                    ))
                  }
                  <td style={{minWidth: "50px"}}>
                    <InputCell
                      isConfirmed={isConfirmed}
                      type="number"
                      onChangeText={(val) => handleDifferenceInSalesNumberChange(rowIndex, val)}  
                      value={rowData.differenceInSalesNumber}
                      textAlignment="right"/>
                  </td>
                  <td style={{minWidth: "110px"}}>
                    <InputCell
                      isConfirmed={isConfirmed}
                      type="text"
                      onChangeText={(val) => handleMemoChange(rowIndex, val)}
                      value={rowData.memo}
                      textAlignment="left"/>
                  </td>
                  {!isConfirmed ? <td><CloseIcon className={styles.close_icon} color="primary" onClick={() => onDelete(rowIndex)}/></td> : <></>}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.inner_functions}>
        {!isConfirmed ? <Button color="primary" variant="outlined" onClick={() => setOpenAddDialog(true)}>＋項目追加</Button> : <></>}
      </div>
    </div>
    {/* dialog to warn user when they try to edit a confirmed invoice  ↓ */}
    <GeneralDialog
      open={openWarningDialog}
      onCancel={() => setOpenWarningDialog(false)}
      cancelText="キャンセル"
      onSave={() => {props.onNotYetConfirmed(); setOpenWarningDialog(false)}}
      saveText="確定を取り消す"
      title="請求確定取り消し"
      onClose={() => setOpenWarningDialog(false)}
    >
      <div>
        既に確定済の請求です。<br/>
        内容を編集するには請求の確定取り消しが必要です。<br/><br/>
      </div>
      <div>請求の確定を取り消しますか？</div>
    </GeneralDialog>
    {!isConfirmed ? <div className={styles.outer_functions}>
      <Button variant="outlined" color="primary" onClick={() => props.onReset()} disabled={!props.isChanged}>編集内容を破棄</Button>
      <Button variant="contained" color="secondary" onClick={() => props.onSave()} disabled={hasError || !props.isChanged}>保存</Button>
    </div> : <></>}
    {
      openAddDialog ? (
        <WrappingFeeDialog
          targetBrand={props.targetInvoice.brand}
          targetInvoice={props.targetInvoice}
          wrappingDeliveryOptions={props.wrappingDeliveryOptions}
          wrappingSizeOptions={props.wrappingSizeOptions}
          onClose={() => setOpenAddDialog(false)}
          open={openAddDialog}
          onSave={items => {onSaveByOfficeWorkDialog(items)}}
          existDates={wrappingExistDates}
        />
      ) : (
        <></>
      )
    }
  </>)
}

export default WrappingFee

export type WrappingFeeProps = {
  targetInvoice: InvoiceMasterData,
  tabValues: WrappingFeeTabType[],
  wrappingSizeOptions: WrappingSizeOption[],
  wrappingDeliveryOptions: DeliveryOption[],
  isChanged: boolean,
  onNotYetConfirmed: () => void,
  onReset: () => void;
  onSave: () => void
  onChange: (items: WrappingFeeTabType[]) => void,
}